// Components
import { useState } from 'react';

import { useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

import { selectedMessagesAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';

type MarkAsReadUnreadProps = {
    isRead: boolean;
    message?: DMConversation;
};

export const MarkAsReadUnread = ({ isRead = false, message }: MarkAsReadUnreadProps) => {
    const [isLoading, setIsLoading] = useState(false);

    const { activeSocialProfile } = useActiveSocialProfile();
    const selectedMessages = useAtomValue(selectedMessagesAtom);
    const { currentSocialInbox } = useSocialInboxNavigation();
    const { messages: dmConversations, mutateMessages } = useSocialInboxDataSource();

    const toggleConversationReadUnread = async () => {
        if (!activeSocialProfile?.id) return;

        setIsLoading(true);

        let messageIds: string[] = [];

        // Determine which IDs to send
        if (message) {
            // Skip if message read status already matches desired state
            const shouldProcess = isRead ? !!message.seen_at : !message.seen_at;

            if (!shouldProcess) {
                setIsLoading(false);
                return;
            }

            messageIds = [message.meta_conversation_id];
        } else if (selectedMessages[currentSocialInbox]?.length) {
            messageIds = selectedMessages[currentSocialInbox].reduce((acc, id) => {
                const conversation = dmConversations?.find(
                    (conversation: DMConversation) =>
                        conversation.meta_conversation_id.toString() === id
                ) as DMConversation | undefined;

                const shouldProcess = isRead ? !!conversation?.seen_at : !conversation?.seen_at;

                if (shouldProcess) acc.push(id);

                return acc;
            }, []);
        }

        if (messageIds.length > 0) {
            DirectMessageService.mutateConversationReadStatus(mutateMessages, messageIds, isRead);

            const isSuccess = await DirectMessageService.toggleConversationReadUnread(
                activeSocialProfile?.id,
                messageIds,
                isRead
            );

            if (!isSuccess) {
                DirectMessageService.mutateConversationReadStatus(
                    mutateMessages,
                    messageIds,
                    !isRead
                );
            }
        }

        setIsLoading(false);
    };

    if (isLoading) return <Loader className="w-3 text-xs text-icoBlack dark:text-white" />;

    return (
        <Icon
            data-testid={isRead ? 'mark-as-unread-icon' : 'mark-as-read-icon'}
            onClick={toggleConversationReadUnread}
            icon={isRead ? 'mail-closed' : 'mail-open'}
            className="cursor-pointer text-xs text-icoBlack dark:text-white"
        />
    );
};
